import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';

export interface KebabMenuOption {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'success' | 'danger';
  disabled?: boolean;
}

interface KebabMenuProps {
  options: KebabMenuOption[];
  ariaLabel?: string;
}

/**
 * ADA-compliant kebab menu component (vertical three dots)
 *
 * Features:
 * - Keyboard navigation (Enter/Space to open, Escape to close, Arrow keys to navigate)
 * - Screen reader support with proper ARIA labels
 * - Focus management
 * - Click outside to close
 * - Portal-based dropdown to avoid overflow clipping
 */
export function KebabMenu({ options, ariaLabel = 'Actions menu' }: KebabMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const buttonId = useId();

  // Calculate dropdown position based on button location
  // Clamps to viewport and flips above button if needed
  const updateDropdownPosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const DROPDOWN_WIDTH = 192; // px, matches w-48
      const GAP = 8; // px gap between button and dropdown
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

      // Horizontal positioning: align right edge, then clamp to viewport
      let left = rect.right - DROPDOWN_WIDTH;
      if (left < 0) {
        left = 0;
      }
      if (viewportWidth && left + DROPDOWN_WIDTH > viewportWidth) {
        left = Math.max(0, viewportWidth - DROPDOWN_WIDTH);
      }

      // Vertical positioning: prefer below button, flip above if needed
      const dropdownHeight = dropdownRef.current?.offsetHeight ?? 0;
      let top = rect.bottom + GAP;
      if (viewportHeight && dropdownHeight) {
        const belowBottom = top + dropdownHeight;
        const aboveTop = rect.top - GAP - dropdownHeight;
        if (belowBottom > viewportHeight && aboveTop >= 0) {
          // Not enough space below; place above the button
          top = aboveTop;
        } else if (belowBottom > viewportHeight) {
          // Clamp to bottom if still overflowing
          top = Math.max(0, viewportHeight - dropdownHeight);
        }
      } else if (viewportHeight && top > viewportHeight) {
        // Fallback clamping when height is unknown
        top = Math.max(0, viewportHeight - GAP);
      }

      setDropdownPosition({ top, left });
    }
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        (!menuRef.current || !menuRef.current.contains(target)) &&
        (!dropdownRef.current || !dropdownRef.current.contains(target))
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Update position when opening and on scroll/resize
  // Use useLayoutEffect for initial position to prevent visual "jump"
  useLayoutEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  // Focus first enabled option when menu opens
  useEffect(() => {
    if (isOpen) {
      const firstEnabledIndex = options.findIndex((opt) => !opt.disabled);
      if (firstEnabledIndex >= 0 && optionRefs.current[firstEnabledIndex]) {
        optionRefs.current[firstEnabledIndex].focus();
      }
    }
  }, [isOpen, options]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      // Open menu with Enter or Space
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        buttonRef.current?.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        {
          const firstEnabled = options.findIndex((opt) => !opt.disabled);
          const currentIndex = Array.from(optionRefs.current).findIndex((ref) => ref === document.activeElement);
          const nextIndex = currentIndex >= 0 ? currentIndex + 1 : firstEnabled >= 0 ? firstEnabled : 0;
          optionRefs.current[nextIndex % options.length]?.focus();
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        {
          const currentIndex = Array.from(optionRefs.current).findIndex((ref) => ref === document.activeElement);
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
          optionRefs.current[prevIndex]?.focus();
        }
        break;
      case 'Home':
        e.preventDefault();
        optionRefs.current[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        optionRefs.current[options.length - 1]?.focus();
        break;
    }
  };

  const handleOptionClick = (option: KebabMenuOption, e: React.MouseEvent) => {
    // Stop event propagation to prevent click from bubbling to backdrop or outside click handlers
    e.stopPropagation();
    e.preventDefault();

    if (!option.disabled) {
      // Close menu and call onClick immediately
      // The dialog has proper pointer-events handling to avoid interference
      setIsOpen(false);
      option.onClick();
    }
  };

  return (
    <div className="relative inline-block text-left" ref={menuRef}>
      <button
        id={buttonId}
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-full transition-colors"
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <svg
          className="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-[9999]"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
          }}
          role="menu"
          aria-orientation="vertical"
          aria-labelledby={buttonId}
        >
          <div className="py-1" role="none">
            {options.map((option, index) => (
              <button
                key={index}
                ref={(el) => { optionRefs.current[index] = el; }}
                type="button"
                onClick={(e) => handleOptionClick(option, e)}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={handleKeyDown}
                disabled={option.disabled}
                className={`block w-full text-left px-4 py-2 text-sm transition-colors hover:bg-gray-100 focus:bg-gray-100 ${
                  option.variant === 'danger'
                    ? 'text-red-700 hover:text-red-900 disabled:text-red-300'
                    : option.variant === 'success'
                    ? 'text-green-700 hover:text-green-900 disabled:text-green-300'
                    : 'text-gray-700 hover:text-gray-900 disabled:text-gray-300'
                } ${option.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} focus:outline-none`}
                role="menuitem"
                tabIndex={-1}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
